import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { useStore } from "@/context/StoreContext";
import { mediaUrl } from "@/lib/api";

const SoundContext = createContext(null);
export const useSound = () => useContext(SoundContext);

// iOS reroutes (and can silence) media when it passes through a Web Audio
// MediaElementSource — so we never build the analyser there; audio plays natively.
const IS_IOS = typeof navigator !== "undefined" && (/iP(ad|hone|od)/.test(navigator.userAgent || "") || (navigator.platform === "MacIntel" && (navigator.maxTouchPoints || 0) > 1));

// Persist customer preference; if they pause, we do not auto-restart.
export const SoundProvider = ({ children }) => {
  const { sound } = useStore();
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(() => load("nba_sound_muted", false));
  const [volume, setVolume] = useState(() => load("nba_sound_vol", 0.4));
  const [playlist, setPlaylist] = useState([]);
  const [idx, setIdx] = useState(0);
  const track = playlist[idx] || null;
  const userPausedRef = useRef(load("nba_sound_off", false));
  const startedRef = useRef(false);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const rafRef = useRef(null);
  const fadeRef = useRef(null);

  // Smoothly ramp the audio volume (used to crossfade between tracks).
  const fadeVol = (a, to, ms) => {
    if (!a) return;
    if (fadeRef.current) clearInterval(fadeRef.current);
    const steps = Math.max(1, Math.round(ms / 40));
    const from = a.volume;
    const step = (to - from) / steps;
    let i = 0;
    fadeRef.current = setInterval(() => {
      i += 1;
      a.volume = Math.min(1, Math.max(0, from + step * i));
      if (i >= steps) { clearInterval(fadeRef.current); fadeRef.current = null; a.volume = Math.min(1, Math.max(0, to)); }
    }, 40);
  };

  function load(k, d) { try { return JSON.parse(localStorage.getItem(k)) ?? d; } catch { return d; } }

  useEffect(() => {
    if (!sound) return;
    // Only fall back to the store default if the visitor has no saved preference.
    if (localStorage.getItem("nba_sound_vol") == null) setVolume(sound.default_volume ?? 0.4);
    if (!sound.enabled) { setPlaylist([]); return; }
    const list = (sound.tracks && sound.tracks.length) ? sound.tracks : (sound.active_track ? [sound.active_track] : []);
    setPlaylist(list);
    const start = list.findIndex((t) => t.id === sound.active_track_id);
    setIdx(start > 0 ? start : 0);
  }, [sound]);

  // Load the current track; advance through the playlist as songs end so the
  // homepage hero video (which follows track.video_url) matches the playing song.
  useEffect(() => {
    if (!track || !track.file_url) return;
    if (!audioRef.current) audioRef.current = new Audio();
    const a = audioRef.current;
    const single = playlist.length <= 1;
    const wasPlaying = startedRef.current && !userPausedRef.current;
    a.src = mediaUrl(track.file_url);
    a.loop = single ? (sound?.loop ?? true) : false;
    a.volume = volume;
    a.muted = muted;
    if (single) {
      const pos = parseFloat(localStorage.getItem("nba_sound_pos") || "0");
      if (pos) a.currentTime = pos;
    }
    if (wasPlaying) {
      if (!single) a.volume = 0;            // start silent, then crossfade the new track up
      a.play().catch(() => {});
      if (!single) fadeVol(a, volume, 1000);
    }
    const onEnded = () => { if (playlist.length > 1) setIdx((i) => (i + 1) % playlist.length); };
    const savepos = () => { if (single) localStorage.setItem("nba_sound_pos", String(a.currentTime || 0)); };
    let fadingOut = false;
    const nearEnd = () => {
      if (single || !a.duration) return;
      if (!fadingOut && a.duration - a.currentTime <= 1.0) { fadingOut = true; fadeVol(a, 0, 1000); }  // fade the current track out before it ends
    };
    a.addEventListener("ended", onEnded);
    a.addEventListener("timeupdate", savepos);
    a.addEventListener("timeupdate", nearEnd);
    return () => { a.removeEventListener("ended", onEnded); a.removeEventListener("timeupdate", savepos); a.removeEventListener("timeupdate", nearEnd); };
    // eslint-disable-next-line
  }, [track, playlist.length]);

  useEffect(() => { if (audioRef.current) audioRef.current.volume = volume; localStorage.setItem("nba_sound_vol", String(volume)); }, [volume]);
  useEffect(() => { if (audioRef.current) audioRef.current.muted = muted; localStorage.setItem("nba_sound_muted", JSON.stringify(muted)); }, [muted]);

  // Web Audio analyser → publishes a live "music level" (0..1) as a CSS var
  // so the storefront imagery can move in time with the track.
  const ensureAnalyser = useCallback(() => {
    const a = audioRef.current;
    if (!a || analyserRef.current || IS_IOS) return;
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      const ctx = new Ctx();
      const src = ctx.createMediaElementSource(a);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256; analyser.smoothingTimeConstant = 0.82;
      src.connect(analyser); analyser.connect(ctx.destination);
      audioCtxRef.current = ctx; analyserRef.current = analyser;
      const data = new Uint8Array(analyser.frequencyBinCount);
      let cur = 0;
      const loop = () => {
        analyser.getByteFrequencyData(data);
        let sum = 0; const n = 24;
        for (let i = 1; i <= n; i++) sum += data[i];
        const target = Math.min(1, (sum / n) / 170);
        cur += (target - cur) * 0.14;
        document.documentElement.style.setProperty("--music-level", cur.toFixed(3));
        rafRef.current = requestAnimationFrame(loop);
      };
      loop();
    } catch (e) { /* analyser unavailable — motion falls back to calm drift */ }
  }, []);

  const doPlay = useCallback(() => {
    const a = audioRef.current;
    if (!a) return;
    a.play().then(() => {
      setPlaying(true); startedRef.current = true;
      ensureAnalyser();
      if (audioCtxRef.current?.state === "suspended") audioCtxRef.current.resume();
    }).catch(() => {});
  }, [ensureAnalyser]);

  useEffect(() => () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    document.documentElement.style.setProperty("--music-level", "0");
  }, []);

  // Begin on first qualifying interaction (autoplay policy compliant)
  useEffect(() => {
    if (!track || !track.file_url) return;
    const onInteract = () => {
      if (startedRef.current || userPausedRef.current) return;
      // The coming-soon countdown owns audio via its teaser video — don't stack a 2nd song.
      if (typeof window !== "undefined" && window.__nbaSuppressSound) return;
      doPlay();
    };
    const events = ["pointerdown", "touchstart", "keydown", "scroll"];
    events.forEach((e) => window.addEventListener(e, onInteract, { passive: true }));
    return () => events.forEach((e) => window.removeEventListener(e, onInteract));
  }, [track, doPlay]);

  const toggle = useCallback(() => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) {
      a.pause(); setPlaying(false);
      userPausedRef.current = true; localStorage.setItem("nba_sound_off", "true");
    } else {
      userPausedRef.current = false; localStorage.setItem("nba_sound_off", "false");
      doPlay();
    }
  }, [playing, doPlay]);

  // duck for audible video
  const duck = useCallback(() => { if (audioRef.current && playing) audioRef.current.pause(); }, [playing]);
  const unduck = useCallback(() => { if (audioRef.current && playing && !userPausedRef.current) audioRef.current.play().catch(() => {}); }, [playing]);
  const next = useCallback(() => { if (playlist.length > 1) setIdx((i) => (i + 1) % playlist.length); }, [playlist.length]);

  return (
    <SoundContext.Provider value={{
      available: !!(track && track.file_url), track, playing, muted, volume,
      setVolume, setMuted, toggle, duck, unduck, next, hasPlaylist: playlist.length > 1,
    }}>
      {children}
    </SoundContext.Provider>
  );
};
